/**
 * Admin: the quotation's letterhead, logo, terms and email wording (SA-1040 §4.5, D12).
 * The page edits one strict document (`PUT /api/admin/quote-document`); the preview is the
 * SERVER's render under the saved settings, never a browser twin.
 */
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react'
import { Alert, AlertDescription, Button, Input, Spinner, Textarea } from '@/components/ui'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { useDefaultTimezone } from '@/hooks/useDefaultTimezone'
import { useQuoteDocumentSettings, useSaveQuoteDocumentSettings } from '@/hooks/useQuoteDocumentSettings'
import { fetchLogoUrl, previewQuoteDocument, uploadLogo } from '@/services/quoteDocument'
import { toForm, toWrite, type Form } from './quoteDocumentForm'
import * as text from './quoteDocumentText'

/** How long a preview's object URL lives: the new tab has loaded the PDF well before then. */
export const PREVIEW_URL_LIFETIME_MS = 60_000

/** The browser refused the tab: save the preview through an anchor instead. */
function downloadUrl(url: string, name: string) {
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
}

const FIELD = 'block space-y-1'
const LABEL = 'text-sm font-medium text-foreground'
const HINT = 'text-xs text-muted-foreground'

function Editor({ initial, updatedByName, updatedAt }: { initial: Form; updatedByName: string | null; updatedAt: string | null }) {
  const save = useSaveQuoteDocumentSettings()
  const zone = useDefaultTimezone()
  const [form, setForm] = useState<Form>(initial)
  const [error, setError] = useState<string | null>(null)
  const [justSaved, setJustSaved] = useState(false)
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  // The one object URL the logo preview holds; replacing, removing or unmounting revokes it.
  const logoUrlRef = useRef<string | null>(null)
  const showLogo = (next: string | null) => {
    if (logoUrlRef.current && logoUrlRef.current !== next) URL.revokeObjectURL(logoUrlRef.current)
    logoUrlRef.current = next
    setLogoUrl(next)
  }
  const showLogoRef = useRef(showLogo)
  showLogoRef.current = showLogo

  useEffect(() => {
    let cancelled = false
    if (initial.logoMediaRef) {
      fetchLogoUrl().then((u) => {
        // a slower, older answer never overwrites a newer one or lands after unmount
        if (cancelled) { if (u) URL.revokeObjectURL(u); return }
        showLogoRef.current(u)
      }).catch(() => { if (!cancelled) showLogoRef.current(null) })
    }
    return () => { cancelled = true }
  }, [initial.logoMediaRef])

  useEffect(() => () => {
    if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current)
  }, [])

  const set = (patch: Partial<Form>) => { setForm((f) => ({ ...f, ...patch })); setJustSaved(false); setError(null) }

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    try {
      await save.mutateAsync(toWrite(form))
      setJustSaved(true)
    } catch (err) {
      setError(text.saveErrorText(err))
    }
  }

  const onLogo = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Cleared after every pick, refused or taken: the browser fires no change for the file the
    // input already holds, so picking it again after a refusal or Remove would do nothing.
    e.target.value = ''
    if (!file) return
    setUploadError(null)
    // `accept=` only filters the picker, and the media upload takes PDFs and text too.
    if (!text.LOGO_TYPES.includes(file.type)) { setUploadError(text.LOGO_BAD_TYPE); return }
    if (file.size > text.LOGO_MAX_BYTES) { setUploadError(text.LOGO_TOO_BIG); return }
    try {
      const ref = await uploadLogo(file)
      set({ logoMediaRef: ref, logoMimeType: file.type })
      showLogo(URL.createObjectURL(file))
    } catch {
      setUploadError(text.UPLOAD_FAILED)
    }
  }

  // The tab opens in the click, before any await: one opened after the render answers is outside
  // the user's gesture, and Safari's popup blocker refuses it. `noopener` would make `open` return
  // null, so the opener is cleared by hand. A failed render closes the blank tab; a refused tab
  // downloads the PDF instead.
  const onPreview = async () => {
    setPreviewing(true); setPreviewError(null)
    const tab = window.open('', '_blank')
    if (tab) tab.opener = null
    try {
      const url = URL.createObjectURL(await previewQuoteDocument())
      if (tab) tab.location.href = url
      else downloadUrl(url, text.PREVIEW_FILE)
      window.setTimeout(() => URL.revokeObjectURL(url), PREVIEW_URL_LIFETIME_MS)
    } catch {
      tab?.close()
      setPreviewError(text.PREVIEW_FAILED)
    } finally {
      setPreviewing(false)
    }
  }

  const saved = text.lastSaved(save.data?.updatedByName ?? updatedByName, save.data?.updatedAt ?? updatedAt, new Date(), zone)

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <Input label={text.COMPANY_NAME} value={form.companyName} onChange={(e) => set({ companyName: e.target.value })} hint={text.COMPANY_HINT} />
      <label className={FIELD}>
        <span className={LABEL}>{text.ADDRESS_LINES}</span>
        <Textarea aria-label={text.ADDRESS_LINES} rows={3} value={form.addressLines} onChange={(e) => set({ addressLines: e.target.value })} />
        <span className={HINT}>{text.LINES_HINT}</span>
      </label>
      <label className={FIELD}>
        <span className={LABEL}>{text.CONTACT_LINES}</span>
        <Textarea aria-label={text.CONTACT_LINES} rows={2} value={form.contactLines} onChange={(e) => set({ contactLines: e.target.value })} />
        <span className={HINT}>{text.LINES_HINT}</span>
      </label>
      <Input label={text.LEGAL_LINE} value={form.legalLine} onChange={(e) => set({ legalLine: e.target.value })} hint={text.LEGAL_HINT} />
      <label className={FIELD}>
        <span className={LABEL}>{text.PAGE_SIZE}</span>
        <select className="rounded-md border border-border bg-background px-2 py-1 text-sm" value={form.pageSize}
          onChange={(e) => set({ pageSize: e.target.value as Form['pageSize'] })}>
          <option value="A4">A4</option>
          <option value="Letter">Letter</option>
        </select>
      </label>
      <div className="space-y-2">
        <span className={LABEL}>{text.LOGO}</span>
        {/* bg-white is deliberate: it stands for the paper the logo prints on */}
        {logoUrl ? <img src={logoUrl} alt="" className="h-12 w-auto rounded border border-border bg-white p-1" />
                 : <p className={HINT}>{text.LOGO_NONE}</p>}
        <label className={FIELD}>
          <span className="sr-only">{text.LOGO_INPUT}</span>
          <input aria-label={text.LOGO_INPUT} type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => void onLogo(e)} />
        </label>
        {form.logoMediaRef && (
          <Button type="button" variant="outline" size="sm" onClick={() => { set({ logoMediaRef: null, logoMimeType: null }); showLogo(null) }}>
            {text.LOGO_REMOVE}
          </Button>
        )}
        <p className={HINT}>{text.LOGO_HINT}</p>
        {uploadError && <Alert variant="destructive"><AlertDescription>{uploadError}</AlertDescription></Alert>}
      </div>
      <label className={FIELD}>
        <span className={LABEL}>{text.TERMS}</span>
        <Textarea aria-label={text.TERMS} rows={6} value={form.terms} onChange={(e) => set({ terms: e.target.value })} />
        <span className={HINT}>{text.TERMS_HINT}</span>
      </label>
      <Input label={text.EMAIL_SUBJECT} value={form.emailSubject} onChange={(e) => set({ emailSubject: e.target.value })} />
      <label className={FIELD}>
        <span className={LABEL}>{text.EMAIL_BODY}</span>
        <Textarea aria-label={text.EMAIL_BODY} rows={8} value={form.emailBody} onChange={(e) => set({ emailBody: e.target.value })} />
        <span className={HINT}>{text.EMAIL_HINT}</span>
      </label>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={save.isPending}>{save.isPending ? text.SAVING : text.SAVE}</Button>
        <Button type="button" variant="outline" disabled={previewing} onClick={() => void onPreview()}>
          {previewing ? text.PREVIEWING : text.PREVIEW}
        </Button>
        <span className={HINT}>{text.PREVIEW_HINT}</span>
        <span role="status" data-testid="save-status" className={HINT}>{justSaved && save.isSuccess ? text.SAVED : ''}</span>
      </div>
      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
      {previewError && <Alert variant="destructive"><AlertDescription>{previewError}</AlertDescription></Alert>}
      {saved && <p className={HINT}>{saved}</p>}
    </form>
  )
}

export function QuoteDocumentPage() {
  const settings = useQuoteDocumentSettings()
  return (
    <PageFrame width="form" className="space-y-6">
      <PageHeader title={text.PAGE_TITLE} subtitle={text.PAGE_SENTENCE} />
      {settings.data === undefined ? (
        settings.isError ? <Alert variant="destructive"><AlertDescription>{text.UNAVAILABLE}</AlertDescription></Alert>
                         : <div className="flex justify-center py-6"><Spinner /></div>
      ) : (
        // mounted once: the form holds what was typed; a save's answer (a new stamp) must not
        // remount it, or the "Saved." line and the save's state go with it
        <Editor initial={toForm(settings.data)}
                updatedByName={settings.data.updatedByName} updatedAt={settings.data.updatedAt} />
      )}
    </PageFrame>
  )
}
